import React, { useEffect, useMemo, useState } from "react";
import "./Expenses.css";
import { supabase } from "./supabaseClient";
import { format, subDays } from "date-fns";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";

const METHOD_OPTS = ["Cash", "Bank"];
const CATEGORY_OPTS = [
  "Other Business Expense",
  "Sundry Expense SKK",
  "Sundry Expense NKP",
  "Paid to Staff",
];

const todayStr = () => format(new Date(), "yyyy-MM-dd");
const toISO = (d) => (d ? format(new Date(d), "yyyy-MM-dd") : "");
const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export default function ExpensesPage() {
  // Form state
  const [date, setDate] = useState(todayStr());
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [staffName, setStaffName] = useState("");
  const [method, setMethod] = useState("Cash");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);

  // Staff from DB
  const [staffList, setStaffList] = useState([]);

  // Filter state
  const [fromDate, setFromDate] = useState(toISO(subDays(new Date(), 7)));
  const [toDate, setToDate] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    fetchRows();
    fetchStaff();
    // eslint-disable-next-line
  }, []);

  async function fetchRows() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("expenses")
        .select("id,date,category,staff_name,description,amount,logged_by,logged_at,payment_method")
        .gte("date", fromDate)
        .lte("date", toDate)
        .order("date", { ascending: true })
        .order("logged_at", { ascending: true });

      if (error) throw error;

      setRows(
        (data || []).map((r) => ({
          id: r.id,
          date: r.date?.slice(0, 10) || "",
          category: r.category || "",
          staff: r.staff_name || "",
          notes: r.description || "",
          amount: n(r.amount),
          method: METHOD_OPTS.includes((r.payment_method || "").trim())
            ? r.payment_method
            : "Cash",
          loggedBy: r.logged_by || "-",
          loggedAt: r.logged_at ? new Date(r.logged_at).toLocaleString() : "-",
        }))
      );
    } catch (err) {
      toast.error("Failed to load expenses: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchStaff() {
    // Assumes you have a "staff" table with a "name" column
    const { data, error } = await supabase.from("employees").select("name").order("name", { ascending: true });
    if (!error && data) setStaffList(data.map(s => s.name));
    // fallback: if no staff table, keep empty
  }

  async function saveEntry() {
    if (!date || !category || amount === "") {
      toast.error("Date, Category and Amount are required.");
      return;
    }
    if (category.toLowerCase() === "paid to staff" && !staffName.trim()) {
      toast.error("Please select Staff Name for 'Paid to Staff'.");
      return;
    }
    setAdding(true);
    try {
      const payload = {
        date,
        category,
        amount: Number(amount),
        description: notes || "",
        staff_name: category.toLowerCase() === "paid to staff" ? staffName : null,
        payment_method: method || "Cash",
      };
      const { error } = editId
        ? await supabase.from("expenses").update(payload).eq("id", editId)
        : await supabase.from("expenses").insert(payload);
      if (error) throw error;

      toast.success(editId ? "Expense updated." : "Expense added.");
      resetForm();
      fetchRows();
    } catch (err) {
      toast.error("Failed to save expense: " + err.message);
    } finally {
      setAdding(false);
    }
  }

  function resetForm() {
    setDate(todayStr());
    setCategory("");
    setAmount("");
    setNotes("");
    setStaffName("");
    setMethod("Cash");
    setEditId(null);
  }

  function handleEdit(row) {
    setDate(row.date || todayStr());
    setCategory(row.category || "");
    setAmount(row.amount?.toString() ?? "");
    setNotes(row.notes || "");
    setStaffName(row.staff || "");
    setMethod(row.method || "Cash");
    setEditId(row.id);
  }

  async function deleteRow(id) {
    if (!window.confirm("Delete this expense?")) return;
    try {
      const { error } = await supabase.from("expenses").delete().eq("id", id);
      if (error) throw error;
      toast.success("Deleted.");
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      toast.error("Could not delete: " + err.message);
    }
  }

  const totals = useMemo(
    () => rows.reduce((a, r) => ({ amount: a.amount + (r.amount || 0) }), { amount: 0 }),
    [rows]
  );

  function exportCSV() {
    if (!rows.length) return;
    const header = [
      "Date","Category","Method","Staff Name","Amount (£)","Notes","Logged By","Logged At",
    ];
    const lines = [
      header.join(","),
      ...rows.map((r) =>
        [
          r.date,
          r.category,
          r.method,
          r.staff,
          r.amount.toFixed(2),
          (r.notes || "").replace(/[\r\n,]+/g, " "),
          r.loggedBy,
          r.loggedAt,
        ].join(",")
      ),
    ];
    saveAs(new Blob([lines.join("\n")], { type: "text/csv" }), `expenses_${fromDate}_${toDate}.csv`);
  }

  // DataGrid columns
  const columns = [
    { field: "date", headerName: "Date", width: 110 },
    { field: "category", headerName: "Category", width: 180 },
    { field: "method", headerName: "Method", width: 100 },
    { field: "staff", headerName: "Staff Name", width: 150 },
    { field: "amount", headerName: "Amount (£)", width: 120, renderCell: p => <span className="green">{p.value.toFixed(2)}</span> },
    { field: "notes", headerName: "Notes", width: 200 },
    { field: "loggedBy", headerName: "Logged By", width: 120 },
    { field: "loggedAt", headerName: "Logged At", width: 170 },
    {
      field: "actions", headerName: "Actions", width: 160, sortable: false, renderCell: (params) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn-edit" onClick={() => handleEdit(params.row)}>Edit</button>
          <button className="btn-delete" onClick={() => deleteRow(params.row.id)}>Delete</button>
        </div>
      )
    }
  ];

  return (
    <div className="main-content expenses-container">
      <div className="main-heading">💰 Expenses</div>

      {/* Expense Entry Section */}
      <div className="form-section">
        <div className="section-title">💸 Expense Entry</div>
        <div className="form-grid">
          <label>
            Date
            <input type="date" value={date} onChange={(e)=>setDate(e.target.value)} />
          </label>
          <label>
            Category
            <select value={category} onChange={(e)=>setCategory(e.target.value)}>
              <option value="">Select</option>
              {CATEGORY_OPTS.map((c)=><option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>
            Amount (£)
            <input type="number" step="0.01" value={amount} onChange={(e)=>setAmount(e.target.value)} />
          </label>
          <label>
            Method
            <select value={method} onChange={(e)=>setMethod(e.target.value)}>
              {METHOD_OPTS.map((m)=><option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>
            Notes
            <input value={notes} onChange={(e)=>setNotes(e.target.value)} />
          </label>
          {category.toLowerCase() === "paid to staff" && (
            <label>
              Staff Name
              <select value={staffName} onChange={(e)=>setStaffName(e.target.value)}>
                <option value="">Select Staff</option>
                {staffList.map(emp=><option key={emp} value={emp}>{emp}</option>)}
              </select>
            </label>
          )}
          <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
            <button className="btn-entry" style={{ marginTop: 22 }} onClick={saveEntry} disabled={adding}>
              {adding ? "Saving…" : editId ? "Update Entry" : "Add Entry"}
            </button>
            {editId && (
              <button className="btn-apply" style={{ marginTop: 22, background: "#eee", color: "#222" }} onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter & Export Section */}
      <div className="form-section">
        <div className="section-title">🔍 Filter & Export</div>
        <div className="form-grid">
          <label>
            From
            <input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} />
          </label>
          <button className="btn-apply" style={{ marginTop: 22 }} onClick={fetchRows}>
            Apply Filter
          </button>
          <button className="btn-export" style={{ marginTop: 22 }} onClick={exportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Data Grid */}
      <div style={{ overflowX: "auto" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={row => row.id}
          pageSize={5}
          rowsPerPageOptions={[5,10,25,50]}
          autoHeight
        />
      </div>

      {/* Totals */}
      <div style={{ marginTop: 16, fontWeight: "bold" }}>
        Total: £{totals.amount.toFixed(2)}
      </div>
    </div>
  );
}